import React, { useContext, useEffect, useState } from 'react';
import { Collapse } from 'react-collapse';
import CreditCheckResult from '../../../models/CreditCheckResult';
import { SalesContext } from '../../../SalesContext';
import { Button } from '../../core/button';
import { Field } from '../../core/field';
import Notification from '../../core/notification';
import { creditCheck, creditCheckDemo } from './service';
import styles from './credit_check_form.module.scss';
import { Modal } from '../../core/modal';
import FontAwesomeIcon from '../../core/fa_icon';

type Props = {
  onResult: (result: CreditCheckResult) => void;
  onEnergyAgreementSubmit: (result: CreditCheckResult) => void;
  isDemo: boolean;
}

enum ErrorCodes {
  NoMatch = '15'
}

/**
 * Form for performing a personal number credit check.
 */
export const CreditCheckForm: React.FC<Props> = ({ onResult, onEnergyAgreementSubmit, isDemo }) => {
  const [ssn, setSsn] = useState('');
  const [isCreditChecking, setCreditChecking] = useState(false);
  const [result, setResult] = useState<CreditCheckResult>(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [hasAcceptedPopup, setHasAcceptedPopup] = useState(false);

  const { creditCheckResult: revisedCreditCheckResult } = useContext(SalesContext);

  useEffect(() => {
    if (hasAcceptedPopup) {
      submitCreditCheck();
    }

  }, [hasAcceptedPopup]);

  async function submitCreditCheck() {
    if (isCreditChecking) {
      return;
    }
    setCreditChecking(true);

    try {
      console.log(`Demo ${isDemo ? "enabled" : "disabled"}`)
      if (isDemo) {
        var { result, errors } = await creditCheckDemo(ssn);
      } else {
        var { result, errors } = await creditCheck(ssn);
      }
    } catch (e) {
      const error = e.message;
      switch (error.Cause_of_Reject) {
        case ErrorCodes.NoMatch:
          Notification.show(`Ingen träff på ${ssn}`);
          break;
      }

      console.error(e);
    }

    if (result !== undefined) {
      setResult(result);
      onResult(result);
    }

    if (errors?.length) {
      errors.forEach(error => Notification.show((<><span style={{ opacity: 0.75 }}>CS:</span> {error}</>)));
    }

    setCreditChecking(false);
  }

  return (
    <form onSubmit={e => {
      e.preventDefault();
      setHasAcceptedPopup(false);
      setModalOpen(true);
      return false;
    }}>
      <h2>
        Kreditupplysning
      </h2>
      <Collapse isOpened>
        <div className={styles.formContent}>
          <Field
            label="Personnummer"
            value={ssn}
            onTextValueChange={setSsn}
            maxLength={13}
            required />

          <Button
            type="submit"
            busy={isCreditChecking}
            weight={!result ? "primary" : "normal"}
            disabled={!ssn?.trim()}>
            {result ? "Kreditupplys igen" : "Kreditupplys"}
          </Button>
        </div>
      </Collapse>

      <Button
        type="submit"
        weight="simple"
        busy={isCreditChecking}
        disabled={isCreditChecking || !revisedCreditCheckResult || result.credibility !== "1"}
        onClick={event => {
          if (!ssn?.trim() || !revisedCreditCheckResult) {
            Notification.show("Ange personnummer");
          } else {
            onEnergyAgreementSubmit(revisedCreditCheckResult);
          }

          event.preventDefault();
          event.stopPropagation();
          return false;
        }}>
        Skapa order
      </Button>

      <Modal open={isModalOpen} onClose={() => {
        setModalOpen(false);
      }} children={
        <div className={styles.popup}>
          <div>
            <FontAwesomeIcon name={"exclamation-triangle"} style={{ color: "red", fontSize: "25px" }} />
            <h3>Viktigt</h3>
          </div>
          <p>Har du frågat kunden om det är okej att ta kreditupplysning?</p>
          <div>
            <Button type='button' weight='primary' onClick={() => {
              setHasAcceptedPopup(true);
              setModalOpen(false);
            }}>Ja</Button>
            <Button type='button' weight='normal' onClick={() => {
              setHasAcceptedPopup(false);
              setModalOpen(false);
              window.location.reload();
            }}>Nej</Button>
          </div>
        </div>
      } />
    </form >
  );
}